import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/lib/app-context";
import { errMsg, dt } from "@/lib/api";
import { PageHeader, Panel, NativeSelect, Empty } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ROLE_HELP = { admin: "Everything incl. users, settings, Zepio credentials", manager: "Products, prices, bill edits, reports, Zepio sync", cashier: "POS, own bills, returns", warehouse: "Stock, racks, purchasing, picking", pending: "No access" };

function UsersTab() {
  const { api, branches, me } = useApp();
  const [d, setD] = useState({ users: [], roles: [] });
  const load = () => api.get("/users").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setRole = async (u, role) => { try { await api.patch(`/users/${u.user_id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const setBranch = async (u, b) => { try { await api.patch(`/users/${u.user_id}/branch`, { branch_id: b || null }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel>
      <p className="text-xs text-muted-foreground mb-3">Staff sign up with their email, then appear here as “pending” until you assign a role.</p>
      <table className="w-full text-sm"><thead className="text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">User</th><th>Role</th><th>Home branch</th><th>Access</th></tr></thead>
        <tbody>{d.users.map((u) => <tr key={u.user_id} className="border-t" data-testid={`user-row-${u.email}`}><td className="py-2"><b>{u.name}</b><div className="text-xs text-muted-foreground">{u.email}</div></td>
          <td><NativeSelect testid={`user-role-select-${u.email}`} className="w-36" value={u.role} onChange={(v) => setRole(u, v)} options={d.roles.map((r) => ({ value: r, label: r }))} /></td>
          <td><NativeSelect className="w-40" value={u.branch_id || ""} onChange={(v) => setBranch(u, v)} placeholder="Any" options={branches.map((b) => ({ value: b.id, label: b.code }))} /></td>
          <td className="text-xs text-muted-foreground">{ROLE_HELP[u.role]}{u.user_id === me.user_id && " (you)"}</td></tr>)}</tbody></table>
    </Panel>
  );
}

function AuditTab() {
  const { api } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/audit", { params: { q } }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, api]);
  return (
    <Panel>
      <Input placeholder="Search action or user" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 max-w-sm" data-testid="audit-search-input" />
      {rows.length ? <table className="w-full text-sm"><thead className="text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">When</th><th>User</th><th>Action</th><th>Details</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} className="border-t align-top"><td className="py-1.5 text-xs whitespace-nowrap">{dt(r.at)}</td><td className="text-xs">{r.user?.name}</td><td className="font-mono text-xs">{r.action}</td>
          <td className="font-mono text-[11px] text-slate-600 break-all">{JSON.stringify(r.details)}</td></tr>)}</tbody></table> : <Empty />}
    </Panel>
  );
}

export default function Users() {
  const { can } = useApp();
  return (
    <div>
      <PageHeader title="Users & Audit" subtitle="Role-based access and a complete log of sensitive actions" />
      <Tabs defaultValue={can("users") ? "users" : "audit"}>
        <TabsList>{can("users") && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}<TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger></TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
